import { Box, Chip, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import { GridColDef } from '@mui/x-data-grid';
import NoTabDataCard from '../../../Hubs/HubDetails/tabs/components/NoTabDataCard';
import MedicationDetailModal from './MedicationModal/MedicationDetailModal';
import MedicationStatsCards from './MedicationStatsCards';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMedicationList } from '../../../../store/hubOwner/actionCreators';
import { RootState } from '../../../../store';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import {
  dimGrey,
  lightBackground1,
  lightBlack,
  successBg,
  warningBg,
} from '../../../../mainTheme';

interface MedicationTabProps {
  ownerId: number;
}

const MedicationTab: React.FC<MedicationTabProps> = ({ ownerId }) => {
  const dispatch = useDispatch();

  const medicationListRaw = useSelector(
    (state: RootState) => state.hubOwner.medicationList,
  );
  const medicationStats = useSelector(
    (state: RootState) => state.hubOwner.medicationStats,
  );
  const medicationStatsOwnerId = useSelector(
    (state: RootState) => state.hubOwner.medicationStatsOwnerId,
  );
  const isLoading = useSelector((state: RootState) => state.hubOwner.isLoading);

  const [selectedMedication, setSelectedMedication] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Ensure each row has a unique id for DataGrid (combine national_drug_code and index )
  const medicationList = useMemo(() => {
    return medicationListRaw
      ? medicationListRaw.map((item, idx) => ({
          ...item,
          id: item.national_drug_code + '-' + idx,
        }))
      : [];
  }, [medicationListRaw]);

  useEffect(() => {
    if (ownerId) {
      dispatch(fetchMedicationList(ownerId));
    }
  }, [dispatch, ownerId]);

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (params: any) => params.value || 'N/A',
    },
    {
      field: 'name',
      headerName: 'Medication name',
      flex: 1.5,
    },
    {
      field: 'medication_type',
      headerName: 'Medication type',
      flex: 1,
      renderCell: (params: any) => params.value || 'N/A',
    },
    {
      field: 'time_tags',
      headerName: 'Prescribed time',
      flex: 1.5,
      renderCell: (params: any) => {
        const getDosageColor = (dosageType: string) => {
          const colorMap: { [key: string]: string } = {
            Morning: warningBg,
            Noon: '#FFF4EB',
            Afternoon: '#FCEFEE',
            Evening: successBg,
            Bedtime: '#F5F9FF',
            Sober: '#F0F0F0',
            PRN: '#F0F0F0',
            Mid: '#F0F0F0',
          };
          return colorMap[dosageType] || '#F0F0F0';
        };

        if (
          !params.value ||
          !Array.isArray(params.value) ||
          params.value.length === 0
        )
          return 'N/A';

        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {params.value.map((tag: string, index: number) => (
              <Tooltip
                key={index}
                title={
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: lightBackground1,
                      border: '1px solid #D4D4D9',
                      borderRadius: '8px',
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                      maxWidth: '18.75rem',
                      color: dimGrey,
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '16px',
                        lineHeight: '24px',
                        letterSpacing: '0.2px',
                      }}
                    >
                      No time available
                    </Typography>
                  </Box>
                }
                placement="bottom-start"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'transparent',
                      p: 0,
                      maxWidth: 'none',
                    },
                  },
                }}
              >
                <Chip
                  label={tag}
                  sx={{
                    padding: '4px 12px',
                    backgroundColor: getDosageColor(tag),
                    color: lightBlack,
                    borderRadius: '999px',
                    fontWeight: 500,
                    lineHeight: '16px',
                    fontSize: '12px',
                    height: '20px',
                    '& .MuiChip-label': {
                      px: 0.5,
                    },
                  }}
                  size="small"
                />
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
  ];

  // this effect updates the selectedMedication when the modal update the status
  useEffect(() => {
    if (isModalOpen && selectedMedication) {
      const updatedMedication = medicationList.find(
        (med) => med.id === selectedMedication.id,
      );
      // Only update if the medication exists and is different by value
      if (
        updatedMedication &&
        JSON.stringify(updatedMedication) !== JSON.stringify(selectedMedication)
      ) {
        setSelectedMedication(updatedMedication);
      }
    }
  }, [medicationList, isModalOpen]);

  const handleRowClick = (row: any) => {
    setSelectedMedication(row);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMedication(null);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <MedicationStatsCards
        stats={medicationStatsOwnerId === ownerId ? medicationStats : null}
      />
      {medicationList && medicationList.length ? (
        <CustomerCareDTable
          rows={medicationList}
          columns={columns}
          pageSize={8}
          defaultSortField="status"
          defaultSortDirection="desc"
          hideViewAllButton
          onRowClick={handleRowClick}
          idField="id"
        />
      ) : (
        <NoTabDataCard />
      )}

      {isModalOpen && selectedMedication && (
        <MedicationDetailModal
          onClose={handleCloseModal}
          rowData={selectedMedication}
          patientId={ownerId}
        />
      )}
    </Box>
  );
};

export default MedicationTab;
